import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Building2, Plus, MapPin, Users, Search, Star, StarOff, MessageSquare, Trash2, Globe, Briefcase,
  X, Send,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { CardSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Recruiter() {
  const [me, setMe] = useState(null);          // recruiter profile or null
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/recruiter/me").then((r) => setMe(r.data || null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="grid md:grid-cols-3 gap-4"><CardSkeleton /><CardSkeleton /><CardSkeleton /></div>;
  if (!me) return <RecruiterRegister onDone={setMe} />;
  return <RecruiterHome recruiter={me} setRecruiter={setMe} />;
}

// --- Register ---
function RecruiterRegister({ onDone }) {
  const [company, setCompany] = useState("");
  const [url, setUrl] = useState("");
  const [role, setRole] = useState("Talent Partner");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!company.trim()) { toast.error("Company required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/recruiter/register", {
        company, company_url: url || undefined, role, bio,
      });
      toast.success("Welcome, recruiter — your portal is live.");
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="recruiter-register">
      <PageHeader
        eyebrow="Module 11"
        title="Recruiter Portal"
        subtitle="Post jobs, shortlist candidates, and message top talent from our public directory. Free for now."
      />
      <form onSubmit={submit} className="max-w-2xl bg-card border border-border rounded-xl p-8 space-y-5">
        <div>
          <Label htmlFor="r-company">Company *</Label>
          <Input id="r-company" data-testid="recruiter-company" required value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme, Inc." className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="r-url">Company URL</Label>
          <Input id="r-url" data-testid="recruiter-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://acme.com" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="r-role">Your role</Label>
          <Input id="r-role" data-testid="recruiter-role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Talent Partner" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="r-bio">Bio (visible to candidates you message)</Label>
          <Textarea id="r-bio" data-testid="recruiter-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Building the ML team at Acme. Ex-Google. Portfolio.acme.com" className="mt-1.5" />
        </div>
        <Button type="submit" disabled={saving} data-testid="recruiter-register-submit" className="gap-2">
          <Building2 size={14} />
          {saving ? "Saving…" : "Activate recruiter portal"}
        </Button>
      </form>
    </div>
  );
}

// --- Home (registered) ---
function RecruiterHome({ recruiter, setRecruiter }) {
  const [tab, setTab] = useState("listings");
  return (
    <div data-testid="recruiter-home">
      <PageHeader
        eyebrow={`${recruiter.company} · ${recruiter.role}`}
        title="Recruiter Portal"
        subtitle="Post jobs. Shortlist candidates. Message top talent."
      />
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-6" data-testid="recruiter-tabs">
          <TabsTrigger value="listings" data-testid="tab-listings">Job Listings</TabsTrigger>
          <TabsTrigger value="candidates" data-testid="tab-candidates">Shortlist</TabsTrigger>
          <TabsTrigger value="search" data-testid="tab-search">Search Talent</TabsTrigger>
        </TabsList>
        <TabsContent value="listings"><ListingsPanel /></TabsContent>
        <TabsContent value="candidates"><ShortlistPanel /></TabsContent>
        <TabsContent value="search"><SearchPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// --- Listings ---
function ListingsPanel() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", company: "", location: "", remote: false, seniority: "mid",
    salary_min: "", salary_max: "", description: "", apply_url: "", tags: "",
  });

  const load = () => api.get("/recruiter/listings").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.company.trim() || !form.description.trim()) {
      toast.error("Title, company, and description required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title, company: form.company, location: form.location,
        remote: form.remote, seniority: form.seniority, description: form.description,
        salary_min: form.salary_min ? Number(form.salary_min) : null,
        salary_max: form.salary_max ? Number(form.salary_max) : null,
        apply_url: form.apply_url || undefined,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      await api.post("/recruiter/listings", payload);
      toast.success("Job listing posted");
      setOpen(false); load();
      setForm({ title: "", company: "", location: "", remote: false, seniority: "mid", salary_min: "", salary_max: "", description: "", apply_url: "", tags: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/recruiter/listings/${id}`);
    toast.success("Listing removed");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">
          {list && `${list.length} listing${list.length === 1 ? "" : "s"}`}
        </div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="listing-new" className="gap-2">
          <Plus size={14} /> Post a job
        </Button>
      </div>

      {!list ? (
        <div className="grid md:grid-cols-2 gap-4"><CardSkeleton /><CardSkeleton /></div>
      ) : list.length === 0 ? (
        <EmptyState icon={Briefcase} title="No listings yet"
          description="Post your first role. It will show up on the public jobs board immediately."
          actionLabel="Post a job" onAction={() => setOpen(true)} testId="listings-empty" />
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {list.map((l) => (
            <div key={l.id} className="bg-card border border-border rounded-xl p-4 card-hover" data-testid={`listing-${l.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-display font-bold text-foreground truncate">{l.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {l.company} · {l.location || (l.remote ? "Remote" : "—")}
                  </div>
                </div>
                <button onClick={() => remove(l.id)} className="text-muted-foreground hover:text-destructive p-1" aria-label="Delete listing">
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="flex items-center gap-1.5 mt-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <span className="px-1.5 py-0.5 border border-border rounded">{l.seniority}</span>
                <span className="px-1.5 py-0.5 border border-border rounded">{l.remote ? "remote" : "onsite"}</span>
                <span className="px-1.5 py-0.5 border border-border rounded">{l.status}</span>
              </div>
              {(l.salary_min || l.salary_max) && (
                <div className="text-xs font-mono text-foreground mt-3">
                  ${(l.salary_min || 0).toLocaleString()} – ${(l.salary_max || 0).toLocaleString()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="listing-form-dialog">
          <DialogHeader><DialogTitle>Post a new job</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <Field label="Job title *" val={form.title} setVal={(v) => setForm({ ...form, title: v })} testId="lf-title" />
            <Field label="Company *" val={form.company} setVal={(v) => setForm({ ...form, company: v })} testId="lf-company" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Location" val={form.location} setVal={(v) => setForm({ ...form, location: v })} testId="lf-location" />
              <div>
                <Label>Seniority</Label>
                <select
                  value={form.seniority}
                  onChange={(e) => setForm({ ...form, seniority: e.target.value })}
                  className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="lf-seniority"
                >
                  <option value="entry">Entry</option>
                  <option value="mid">Mid</option>
                  <option value="senior">Senior</option>
                  <option value="lead">Lead</option>
                  <option value="exec">Executive</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.remote} onChange={(e) => setForm({ ...form, remote: e.target.checked })} data-testid="lf-remote" />
              <span className="text-sm">Remote-friendly</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Salary min ($)" type="number" val={form.salary_min} setVal={(v) => setForm({ ...form, salary_min: v })} testId="lf-smin" />
              <Field label="Salary max ($)" type="number" val={form.salary_max} setVal={(v) => setForm({ ...form, salary_max: v })} testId="lf-smax" />
            </div>
            <Field label="Apply URL" val={form.apply_url} setVal={(v) => setForm({ ...form, apply_url: v })} testId="lf-apply" />
            <Field label="Tags (comma-separated)" val={form.tags} setVal={(v) => setForm({ ...form, tags: v })} testId="lf-tags" />
            <div>
              <Label htmlFor="lf-desc">Description *</Label>
              <Textarea id="lf-desc" rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="lf-desc" className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="listing-submit">{saving ? "Posting…" : "Post job"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, val, setVal, testId, type = "text" }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={val} onChange={(e) => setVal(e.target.value)} data-testid={testId} className="mt-1.5" />
    </div>
  );
}

// --- Shortlist ---
function ShortlistPanel() {
  const [list, setList] = useState(null);
  const [msgTarget, setMsgTarget] = useState(null);
  useEffect(() => { api.get("/recruiter/shortlist").then((r) => setList(r.data)); }, []);
  const remove = async (username) => {
    await api.post("/recruiter/shortlist", { username, action: "remove" });
    setList(list.filter((c) => c.username !== username));
    toast.success("Removed from shortlist");
  };
  if (!list) return <CardSkeleton />;
  if (list.length === 0) return (
    <EmptyState
      icon={Star}
      title="No candidates shortlisted"
      description="Head to the Search tab to find candidates and add them to your shortlist."
      testId="shortlist-empty"
    />
  );
  return (
    <>
      <div className="grid md:grid-cols-2 gap-3">
        {list.map((c) => (
          <div key={c.username} className="bg-card border border-border rounded-xl p-4" data-testid={`shortlist-${c.username}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-semibold text-foreground truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground truncate">{c.current_role || "—"}</div>
                {c.location && <div className="text-[11px] font-mono text-muted-foreground mt-0.5 inline-flex items-center gap-1"><MapPin size={11} /> {c.location}</div>}
              </div>
              <div className="flex items-center gap-1">
                <Link to={`/u/${c.username}`} target="_blank" className="text-muted-foreground hover:text-foreground p-1" aria-label="View profile"><Globe size={13} /></Link>
                <button onClick={() => setMsgTarget(c)} className="text-muted-foreground hover:text-primary p-1" aria-label="Message candidate"><MessageSquare size={13} /></button>
                <button onClick={() => remove(c.username)} className="text-muted-foreground hover:text-destructive p-1" aria-label="Remove"><StarOff size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <MessageDialog target={msgTarget} onClose={() => setMsgTarget(null)} />
    </>
  );
}

// --- Search ---
function SearchPanel() {
  const [role, setRole] = useState("");
  const [location, setLocation] = useState("");
  const [skill, setSkill] = useState("");
  const [data, setData] = useState(null);
  const [shortlisted, setShortlisted] = useState({});
  const [msgTarget, setMsgTarget] = useState(null);

  const search = async () => {
    const { data } = await api.get("/directory", {
      params: { role, location, skill, limit: 24 },
    });
    setData(data);
  };
  useEffect(() => { search(); }, []);
  useEffect(() => {
    api.get("/recruiter/shortlist").then((r) => {
      const map = {};
      r.data.forEach((c) => (map[c.username] = true));
      setShortlisted(map);
    });
  }, []);

  const toggle = async (username) => {
    const action = shortlisted[username] ? "remove" : "add";
    try {
      await api.post("/recruiter/shortlist", { username, action });
      setShortlisted((prev) => ({ ...prev, [username]: !prev[username] }));
      toast.success(action === "add" ? "Added to shortlist" : "Removed");
    } catch (e) {
      toast.error("Action failed");
    }
  };

  return (
    <>
      <div className="grid md:grid-cols-4 gap-3 mb-6">
        <Input placeholder="Role…" value={role} onChange={(e) => setRole(e.target.value)} data-testid="search-role" />
        <Input placeholder="Location…" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="search-location" />
        <Input placeholder="Skill…" value={skill} onChange={(e) => setSkill(e.target.value)} data-testid="search-skill" />
        <Button onClick={search} data-testid="search-submit" className="gap-2"><Search size={14} /> Search</Button>
      </div>
      {!data ? <CardSkeleton /> : data.profiles.length === 0 ? (
        <EmptyState icon={Users} title="No matches" description="Try broader filters." testId="search-empty" />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.profiles.map((p) => (
            <div key={p.username} className="bg-card border border-border rounded-xl p-4 card-hover" data-testid={`candidate-${p.username}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-foreground truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{p.current_role || "—"}</div>
                  {p.location && <div className="text-[11px] font-mono text-muted-foreground mt-0.5 inline-flex items-center gap-1"><MapPin size={11} /> {p.location}</div>}
                </div>
                <button
                  onClick={() => toggle(p.username)}
                  className={`p-1 transition-colors ${shortlisted[p.username] ? "text-[hsl(var(--warning))]" : "text-muted-foreground hover:text-[hsl(var(--warning))]"}`}
                  aria-label="Toggle shortlist"
                  data-testid={`toggle-shortlist-${p.username}`}
                >
                  <Star size={14} fill={shortlisted[p.username] ? "currentColor" : "none"} />
                </button>
              </div>
              {(p.top_skills || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {p.top_skills.slice(0, 4).map((s) => (
                    <span key={s} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border border-border rounded px-1.5 py-0.5">{s}</span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2">
                <Link to={`/u/${p.username}`} target="_blank" className="text-xs text-primary hover:underline">View profile</Link>
                <button onClick={() => setMsgTarget(p)} className="ml-auto text-xs text-primary hover:underline inline-flex items-center gap-1">
                  <MessageSquare size={12} /> Message
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <MessageDialog target={msgTarget} onClose={() => setMsgTarget(null)} />
    </>
  );
}

// --- Message dialog ---
function MessageDialog({ target, onClose }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [roleOffered, setRoleOffered] = useState("");
  const [salary, setSalary] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (target) {
      setSubject(""); setMessage(""); setRoleOffered(""); setSalary("");
    }
  }, [target]);

  const send = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Subject and message required"); return; }
    setSending(true);
    try {
      await api.post("/recruiter/message", {
        username: target.username, subject, message,
        role_offered: roleOffered || undefined, salary_range: salary || undefined,
      });
      toast.success(`Message sent to ${target.name}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="message-dialog">
        {target && (
          <>
            <DialogHeader><DialogTitle>Message {target.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Subject *" val={subject} setVal={setSubject} testId="msg-subject" />
              <Field label="Role you're pitching (optional)" val={roleOffered} setVal={setRoleOffered} testId="msg-role" />
              <Field label="Salary range (optional)" val={salary} setVal={setSalary} testId="msg-salary" />
              <div>
                <Label htmlFor="msg-body">Message *</Label>
                <Textarea id="msg-body" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="msg-body" className="mt-1.5" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={send} disabled={sending} data-testid="msg-send" className="gap-2">
                <Send size={13} /> {sending ? "Sending…" : "Send"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
